import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Button } from "reactstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChartColumn, faClose, faExpand, faRotateRight } from "@fortawesome/free-solid-svg-icons";
import { ResponsiveBar } from "@nivo/bar";
import { ResponsiveRadar } from "@nivo/radar";
import getDataFromApi from "../../components/getDataFromApi";
import Loader from "../../components/Loader";
import BarNivo from "../project/train/graph/BarNivo";
import PieNivo from "../project/train/graph/PieNivo";
import HeatMapGraph from "../project/train/graph/HeatMapGraph";
import LineNivo from "../project/train/graph/LineNivo";
import RadialChart from "../project/train/graph/RadialChart";
import RadarChart from "../project/train/graph/RadarChart";
import nivoTheme from "../project/train/graph/nivoTheme";
import "./EvaluationAnalytics.scss";

const PAGE_SIZE = 2;
const MODEL_PAGE_SIZE = 10;

const getStatisticsSections = (response) =>
  Object.entries(response || {})
    .filter(([, value]) => value?.type === "evaluation_statistics")
    .map(([key, value]) => ({ key, ...value }));

const formatGeneratedOn = (value) => {
  if (!value) return "";

  const date = new Date(String(value).replace(" ", "T"));
  return Number.isNaN(date.getTime()) ? value : date.toLocaleString();
};

const isIntegerCountAxis = (chart) => {
  const axisHint = `${chart?.ylabel || ""} ${chart?.title || ""}`.toLowerCase();
  if (/(ms|%|rate|success|utilization|tps|ttft)/.test(axisHint)) return false;
  return /(evaluations|count|users|models|devices|non-complete)/.test(axisHint);
};

function EntitySelectorControls({ chartKey, label, options, selectedId, onChange }) {
  if (!options.length) return null;
  const controlId = `${chartKey}-entity`;
  return (
    <div className="evaluation-analytics-chart-controls">
      <label className="evaluation-analytics-chart-controls__label" htmlFor={controlId}>
        {label}
      </label>
      <select
        id={controlId}
        className="evaluation-analytics-chart-controls__select"
        value={selectedId}
        onChange={(event) => onChange(event.target.value)}
      >
        {options.map((option) => (
          <option key={option.id} value={option.id}>
            {option.label || option.id}
          </option>
        ))}
      </select>
    </div>
  );
}

function useEntitySelector(chart) {
  const selector = chart?.modelSelector;
  const options = Array.isArray(selector?.options) ? selector.options : [];
  const label = selector?.label || "Model";
  const [selectedId, setSelectedId] = useState(
    () => selector?.defaultId || options[0]?.id || ""
  );

  useEffect(() => {
    if (!options.length) return;
    const stillValid = options.some((option) => option.id === selectedId);
    if (!stillValid) {
      setSelectedId(selector?.defaultId || options[0].id);
    }
  }, [options, selectedId, selector?.defaultId]);

  const selectedLabel =
    options.find((option) => option.id === selectedId)?.label || selectedId;

  return { options, label, selectedId, setSelectedId, selectedLabel };
}

function SelectableLineChart({ chartKey, chart, title }) {
  const { options, label, selectedId, setSelectedId } =
    useEntitySelector(chart);
  const seriesByModel = chart?.seriesByModel || {};

  const lineData = useMemo(() => {
    if (selectedId && Array.isArray(seriesByModel[selectedId])) {
      return seriesByModel[selectedId];
    }
    return chart.data;
  }, [chart.data, selectedId, seriesByModel]);

  if (!Array.isArray(lineData) || !lineData.length) return null;

  return (
    <div
      className="evaluation-analytics-chart evaluation-analytics-chart--wide container-card"
      key={chartKey}
    >
      <EntitySelectorControls
        chartKey={chartKey}
        label={label}
        options={options}
        selectedId={selectedId}
        onChange={setSelectedId}
      />
      <LineNivo
        singleGraphData={lineData}
        title={title}
        xLabel={chart.xlabel}
        yLabel={chart.ylabel}
        scale={chart.x_scale || chart.scale}
        enableArea={chart.enableArea !== false}
        showLegend
      />
    </div>
  );
}

function SelectablePieChart({ chartKey, chart, title }) {
  const { options, label, selectedId, setSelectedId } =
    useEntitySelector(chart);
  const piesByModel = chart?.piesByModel || {};

  const pieData = useMemo(() => {
    if (selectedId && Array.isArray(piesByModel[selectedId])) {
      return piesByModel[selectedId];
    }
    return chart.data;
  }, [chart.data, piesByModel, selectedId]);

  if (!Array.isArray(pieData) || !pieData.length) return null;

  return (
    <div
      className="evaluation-analytics-chart evaluation-analytics-chart--pie"
      key={chartKey}
    >
      <EntitySelectorControls
        chartKey={chartKey}
        label={label}
        options={options}
        selectedId={selectedId}
        onChange={setSelectedId}
      />
      <PieNivo
        data={pieData}
        title={title}
        xLabel={chart.xlabel}
        yLabel={chart.ylabel}
        height="360px"
        useDatumColors
        borderless
      />
    </div>
  );
}

function StatisticsChart({ chartKey, chart }) {
  const title = chart.title || chartKey.replaceAll("_", " ");
  const chartType = chart?.type;

  if (!Array.isArray(chart?.data) || !chart.data.length) return null;

  if (chartType === "pie_nivo") {
    if (chart?.modelSelector || chart?.piesByModel) {
      return (
        <SelectablePieChart chartKey={chartKey} chart={chart} title={title} />
      );
    }
    return (
      <div
        className="evaluation-analytics-chart evaluation-analytics-chart--pie"
        key={chartKey}
      >
        <PieNivo
          data={chart.data}
          title={title}
          xLabel={chart.xlabel}
          yLabel={chart.ylabel}
          height="360px"
          useDatumColors
          borderless
        />
      </div>
    );
  }

  if (chartType === "heatmap_nivo") {
    const rowCount = Array.isArray(chart.data) ? chart.data.length : 0;
    const longestLabel = Array.isArray(chart.data)
      ? chart.data.reduce((max, row) => Math.max(max, String(row?.id || "").length), 0)
      : 0;
    const rowHeight = longestLabel > 20 ? 38 : 34;
    const heatmapHeight = Math.max(420, Math.min(960, 150 + rowCount * rowHeight));
    return (
      <div
        className="evaluation-analytics-chart evaluation-analytics-chart--wide container-card"
        key={chartKey}
        style={{ minHeight: heatmapHeight + 48 }}
      >
        <HeatMapGraph
          data={chart.data}
          title={title}
          xLabel={chart.xlabel}
          yLabel={chart.ylabel}
          valueLegendTitle={chart.valueLegendTitle}
          height={heatmapHeight}
        />
      </div>
    );
  }

  if (chartType === "line_nivo") {
    return (
      <SelectableLineChart chartKey={chartKey} chart={chart} title={title} />
    );
  }

  if (chartType === "radial_nivo" || chartType === "radial") {
    return (
      <div className="evaluation-analytics-chart container-card" key={chartKey}>
        <RadialChart data={chart.data} title={title} height={380} />
      </div>
    );
  }

  if (chartType === "radar_nivo" || chartType === "radar") {
    return (
      <div className="evaluation-analytics-chart container-card" key={chartKey}>
        <RadarChart
          data={chart.data}
          keys={chart.keys}
          indexBy={chart.indexBy || "metric"}
          title={title}
          height={380}
          maxValue={chart.maxValue ?? 100}
        />
      </div>
    );
  }

  if (["bar_nivo", "grouped_bar_nivo"].includes(chartType)) {
    const hasMultipleSeries = getNumericKeys(chart.data).length > 1;
    const isModelChipsetAxis =
      chart.xlabel === "Model · Chipset"
      || chart.xlabel === "Task · Chipset";
    const isTaskTypeAxis = chart.xlabel === "Task Type";
    const isModelAxis = chart.xlabel === "Model";
    const isChipsetAxis = chart.xlabel === "Chipset";
    const isEvaluationsByChipset =
      isChipsetAxis && /evaluation/i.test(`${title} ${chart.ylabel || ""}`);
    const isDeviceAxis = chart.xlabel === "Device";
    const isUserStacked =
      chart.xlabel === "User" && chart.groupMode === "stacked";
    const isWide =
      isModelChipsetAxis
      || isTaskTypeAxis
      || isModelAxis
      || isChipsetAxis
      || isDeviceAxis
      || isUserStacked
      || hasMultipleSeries
      || !!chart.showLegend;
    const categoryCount = Array.isArray(chart.data) ? chart.data.length : 0;
    const barHeight = isModelChipsetAxis
      ? Math.max(420, Math.min(640, 300 + categoryCount * 10))
      : isTaskTypeAxis || isModelAxis
        ? Math.max(440, Math.min(680, 300 + categoryCount * 14))
        : isUserStacked
          ? Math.max(440, Math.min(560, 360 + (chart.keys?.length || 4) * 8))
          : isWide
            ? 420
            : 400;
    const showLegend = !!chart.showLegend || hasMultipleSeries;
    return (
      <div
        className={`evaluation-analytics-chart container-card${
          isWide ? " evaluation-analytics-chart--wide" : ""
        }`}
        key={chartKey}
        style={{ minHeight: barHeight + 24 }}
      >
        <BarNivo
          data={chart.data}
          title={title}
          xlabel={chart.xlabel}
          ylabel={chart.ylabel}
          height={`${barHeight}px`}
          cssManage={true}
          integerYAxisTicks={isIntegerCountAxis(chart)}
          truncateAxisLabelsAt={
            isModelChipsetAxis || isTaskTypeAxis || isModelAxis || isDeviceAxis
              ? 18
              : 16
          }
          axisBottomMargin={150}
          axisBottomLegendOffset={115}
          showGridY={true}
          labelField={chart.labelField}
          showLegend={showLegend}
          groupMode={
            isEvaluationsByChipset
              ? "stacked"
              : chart.groupMode
            || (chartType === "grouped_bar_nivo" ? "grouped" : "stacked")
          }
        />
      </div>
    );
  }

  return null;
}

const CHART_COLORS = ["#2f78ed", "#8b5cf6", "#f97316", "#14b8a6", "#eab308", "#ec4899"];
const CHART_METADATA_KEYS = new Set(["id", "name", "label", "category", "class"]);
const RADAR_THEME = {
  ...nivoTheme,
  tooltip: {
    ...nivoTheme.tooltip,
    zIndex: 1000,
  },
};

const prettifyLabel = (value) =>
  String(value || "")
    .replaceAll("_", " ")
    .replace(/\b\w/g, (letter) => letter.toUpperCase());

const getCategory = (row, index) =>
  String(row?.id ?? row?.name ?? row?.label ?? row?.category ?? `Item ${index + 1}`);

const getNumericKeys = (rows) => {
  const keys = new Set();
  rows.forEach((row) => {
    Object.entries(row || {}).forEach(([key, value]) => {
      const normalizedKey = key.toLowerCase();
      if (
        !CHART_METADATA_KEYS.has(normalizedKey) &&
        !normalizedKey.includes("color") &&
        value !== "" &&
        Number.isFinite(Number(value))
      ) {
        keys.add(key);
      }
    });
  });
  return [...keys];
};

const getChartMetrics = (charts) =>
  Object.entries(charts || {}).flatMap(([chartKey, chart]) => {
    if (!["bar_nivo", "grouped_bar_nivo"].includes(chart?.type) || !Array.isArray(chart.data)) {
      return [];
    }

    const numericKeys = getNumericKeys(chart.data);
    return numericKeys.map((valueKey) => ({
      id: `${chartKey}-${valueKey}`,
      label: numericKeys.length === 1
        ? chart.title || prettifyLabel(chartKey)
        : `${chart.title || prettifyLabel(chartKey)} · ${prettifyLabel(valueKey)}`,
      shortLabel: numericKeys.length === 1 ? chart.title || prettifyLabel(valueKey) : prettifyLabel(valueKey),
      unit: chart.ylabel || "",
      values: chart.data.map((row, index) => ({
        category: getCategory(row, index),
        value: Number(row?.[valueKey]),
      })).filter((item) => Number.isFinite(item.value)),
    }));
  });

const isLowerBetterMetric = (metric) =>
  /latency|time|duration|cpu|memory|power|size|error/i.test(`${metric.label} ${metric.unit}`);

const formatChartValue = (value) =>
  Number(value).toLocaleString(undefined, { maximumFractionDigits: 2 });

const getRadarMetricLabel = (metric, compact = false) => {
  if (!compact) return metric.shortLabel;

  return String(metric.shortLabel || "")
    .replace(/^Average\s+/i, "")
    .replace(/\s+by\s+Chipset/i, "")
    .replace(/Time to First Token/i, "TTFT")
    .replace(/Embedding Generation Time/i, "Embedding Time")
    .replace(/Context Window Size/i, "Context Size")
    .trim();
};

function useMediaQuery(query) {
  const [matches, setMatches] = useState(() =>
    typeof window !== "undefined" ? window.matchMedia(query).matches : false
  );

  useEffect(() => {
    const mediaQuery = window.matchMedia(query);
    const updateMatches = (event) => setMatches(event.matches);

    setMatches(mediaQuery.matches);
    mediaQuery.addEventListener("change", updateMatches);
    return () => mediaQuery.removeEventListener("change", updateMatches);
  }, [query]);

  return matches;
}

function RadarValueTooltip({ index, data, metrics, compact }) {
  const metric = metrics.find((item) => getRadarMetricLabel(item, compact) === index);
  const rawValues = new Map(metric?.values.map((item) => [item.category, item.value]) || []);

  return (
    <div className="evaluation-analytics-chart-tooltip evaluation-analytics-radar-tooltip">
      <strong>{index}</strong>
      {data.map((item) => (
        <span key={item.id}>
          <i style={{ backgroundColor: item.color }} />
          <span>{item.id}</span>
          <b>
            {formatChartValue(rawValues.get(item.id) ?? 0)}
            {metric?.unit ? ` ${metric.unit}` : ""}
          </b>
        </span>
      ))}
    </div>
  );
}

function RadarTooltipTracker({ index, data, onChange }) {
  useEffect(() => {
    onChange({ index, data });
    return () => onChange(null);
  }, [data, index, onChange]);

  return null;
}

function ComparisonLineLayer({ metrics, primaryMetric, xScale, innerHeight }) {
  const lineMetrics = metrics.filter((metric) => metric.id !== primaryMetric.id);

  return (
    <g className="evaluation-analytics-line-layer">
      {[0, 25, 50, 75, 100].map((tick) => (
        <g key={tick} transform={`translate(0, ${innerHeight - ((tick / 100) * innerHeight)})`}>
          <text x={xScale.range()[1] + 12} y={4}>{tick}%</text>
        </g>
      ))}
      {lineMetrics.map((metric, metricIndex) => {
        const maximum = Math.max(...metric.values.map(({ value }) => Math.abs(value)), 1);
        const points = metric.values.map(({ category, value }) => ({
          category,
          value,
          x: xScale(category) + (xScale.bandwidth() / 2),
          y: innerHeight - ((Math.abs(value) / maximum) * innerHeight),
        })).filter(({ x, y }) => Number.isFinite(x) && Number.isFinite(y));
        const color = CHART_COLORS[(metricIndex + 1) % CHART_COLORS.length];

        return (
          <g key={metric.id}>
            <polyline
              fill="none"
              stroke={color}
              strokeWidth="2.5"
              strokeLinejoin="round"
              strokeLinecap="round"
              points={points.map(({ x, y }) => `${x},${y}`).join(" ")}
            />
            {points.map((point) => (
              <circle key={point.category} cx={point.x} cy={point.y} r="4" fill={color} stroke="var(--bg-color8)" strokeWidth="2">
                <title>{`${metric.label} — ${point.category}: ${formatChartValue(point.value)} ${metric.unit}`}</title>
              </circle>
            ))}
          </g>
        );
      })}
    </g>
  );
}

function OptimizedModelCharts({ charts, sectionTitle }) {
  const metrics = useMemo(() => getChartMetrics(charts), [charts]);
  const isCompactRadar = useMediaQuery("(max-width: 1180px)");
  const normalizedSectionTitle = String(sectionTitle || "").toLowerCase();
  const isPerformanceByChipset =
    normalizedSectionTitle.includes("performance") && normalizedSectionTitle.includes("chipset");
  const isEvaluationsByChipset =
    normalizedSectionTitle.includes("evaluation") && normalizedSectionTitle.includes("chipset");
  const suggestedPrimary = useMemo(
    () => metrics.find((metric) => /throughput|fps|performance|evaluation/i.test(metric.label)) || metrics[0],
    [metrics]
  );
  const categories = useMemo(
    () => [...new Set(metrics.flatMap((metric) => metric.values.map(({ category }) => category)))],
    [metrics]
  );
  const radarCategories = useMemo(() => categories.slice(0, 5), [categories]);
  const radarData = useMemo(() => metrics.map((metric) => {
    const row = { metric: getRadarMetricLabel(metric, isCompactRadar) };
    radarCategories.forEach((category) => {
      row[category] = 0;
    });
    const maximum = Math.max(...metric.values.map(({ value }) => Math.abs(value)), 1);
    const positiveValues = metric.values.map(({ value }) => Math.abs(value)).filter((value) => value > 0);
    const minimum = positiveValues.length ? Math.min(...positiveValues) : 0;
    metric.values.forEach(({ category, value }) => {
      const magnitude = Math.abs(value);
      row[category] = isLowerBetterMetric(metric)
        ? (magnitude > 0 ? (minimum / magnitude) * 100 : 100)
        : (magnitude / maximum) * 100;
    });
    return row;
  }), [isCompactRadar, metrics, radarCategories]);
  const [primaryMetricId, setPrimaryMetricId] = useState(suggestedPrimary?.id);
  const [selectedRadarCategories, setSelectedRadarCategories] = useState(radarCategories);
  const [radarTooltip, setRadarTooltip] = useState(null);
  const [barTooltip, setBarTooltip] = useState(null);
  const [expandedChart, setExpandedChart] = useState(null);
  const renderRadarTooltipTracker = useCallback(({ index, data }) => (
    <RadarTooltipTracker
      index={index}
      data={data}
      onChange={setRadarTooltip}
    />
  ), []);
  const activeRadarCategories = useMemo(
    () => radarCategories.filter((category) => selectedRadarCategories.includes(category)),
    [radarCategories, selectedRadarCategories]
  );
  const radarColors = useMemo(
    () => activeRadarCategories.map(
      (category) => CHART_COLORS[radarCategories.indexOf(category) % CHART_COLORS.length]
    ),
    [activeRadarCategories, radarCategories]
  );

  useEffect(() => {
    if (!metrics.some((metric) => metric.id === primaryMetricId)) {
      setPrimaryMetricId(suggestedPrimary?.id);
    }
  }, [metrics, primaryMetricId, suggestedPrimary]);

  useEffect(() => {
    setSelectedRadarCategories(radarCategories);
  }, [radarCategories]);

  useEffect(() => {
    if (!expandedChart) return undefined;

    const closeOnEscape = (event) => {
      if (event.key === "Escape") setExpandedChart(null);
    };

    document.addEventListener("keydown", closeOnEscape);
    return () => document.removeEventListener("keydown", closeOnEscape);
  }, [expandedChart]);

  if (!metrics.length) return null;

  const primaryMetric = metrics.find((metric) => metric.id === primaryMetricId) || suggestedPrimary;
  const primaryValues = new Map(primaryMetric.values.map((item) => [item.category, item.value]));
  const barData = categories.map((category) => ({ category, value: primaryValues.get(category) ?? 0 }));
  const groupedBarData = categories.map((category) => {
    const row = { category };
    metrics.forEach((metric) => {
      row[metric.id] = metric.values.find((item) => item.category === category)?.value ?? 0;
    });
    return row;
  });
  const toggleRadarCategory = (category) => {
    setSelectedRadarCategories((current) => {
      if (!current.includes(category)) {
        return radarCategories.filter((item) => item === category || current.includes(item));
      }

      return current.length > 1 ? current.filter((item) => item !== category) : current;
    });
  };
  const showComparisonChart = !isPerformanceByChipset;
  const showRadarChart = !isEvaluationsByChipset && metrics.length >= 3 && radarCategories.length >= 1;
  const pureBarChart = isEvaluationsByChipset;
  const usesIntegerYAxis = pureBarChart || isIntegerCountAxis({
    title: sectionTitle,
    ylabel: primaryMetric.unit,
  });
  const integerAxisValues = pureBarChart
    ? groupedBarData.map((row) =>
        metrics.reduce((total, metric) => total + (Number(row[metric.id]) || 0), 0)
      )
    : primaryMetric.values.map(({ value }) => value);
  const integerMaximum = usesIntegerYAxis ? Math.max(0, ...integerAxisValues) : 0;
  const integerTickStep = Math.max(1, Math.ceil(integerMaximum / 10));
  const integerAxisMaximum = Math.max(
    integerTickStep,
    Math.ceil(integerMaximum / integerTickStep) * integerTickStep
  );
  const integerTickValues = usesIntegerYAxis
    ? Array.from(
        { length: Math.floor(integerAxisMaximum / integerTickStep) + 1 },
        (_, index) => index * integerTickStep
      )
    : undefined;

  const renderExpandButton = (chartName, label) => (
    <button
      type="button"
      className="evaluation-analytics-expand-button"
      title={`View ${label} in full screen`}
      aria-label={`View ${label} in full screen`}
      onClick={() => setExpandedChart(chartName)}
    >
      <FontAwesomeIcon icon={faExpand} />
    </button>
  );

  const renderComparisonChart = (isFullView = false) => (
    <>
      <div className="evaluation-analytics-chart-heading">
        <div>
          <h6>{sectionTitle || "Hardware performance comparison"}</h6>
        </div>
        <div className="evaluation-analytics-chart-heading__actions">
          {!pureBarChart && metrics.length > 1 && (
            <div className="evaluation-analytics-metric-switcher" aria-label="Select bar metric">
              {metrics.map((metric) => (
                <button
                  className={metric.id === primaryMetric.id ? "active" : ""}
                  key={metric.id}
                  onClick={() => setPrimaryMetricId(metric.id)}
                  title={`Show ${metric.label} as bars`}
                  type="button"
                >
                  {metric.shortLabel}
                </button>
              ))}
            </div>
          )}
          {!isFullView && renderExpandButton("comparison", sectionTitle || "comparison chart")}
        </div>
      </div>
      <div className="evaluation-analytics-comparison-content">
        <div
          className={`evaluation-analytics-comparison-chart${isFullView ? "" : " is-click-expandable"}`}
          role={isFullView ? undefined : "button"}
          tabIndex={isFullView ? undefined : 0}
          aria-label={isFullView ? undefined : `Expand ${sectionTitle || "comparison chart"}`}
          onClick={isFullView ? undefined : () => setExpandedChart("comparison")}
          onKeyDown={isFullView ? undefined : (event) => {
            if (event.key === "Enter" || event.key === " ") {
              event.preventDefault();
              setExpandedChart("comparison");
            }
          }}
        >
          <ResponsiveBar
            data={pureBarChart ? groupedBarData : barData}
            keys={pureBarChart ? metrics.map((metric) => metric.id) : ["value"]}
            indexBy="category"
            margin={{ top: 18, right: pureBarChart ? 28 : metrics.length > 1 ? 52 : 18, bottom: 62, left: 58 }}
            padding={pureBarChart ? 0.3 : 0.48}
            groupMode="stacked"
            maxValue={usesIntegerYAxis ? integerAxisMaximum : "auto"}
            colors={pureBarChart ? CHART_COLORS : [CHART_COLORS[0]]}
            borderRadius={3}
            enableLabel={false}
            enableGridY
            axisBottom={{ tickRotation: categories.length > 4 ? -25 : 0, tickPadding: 8 }}
            axisLeft={{
              tickValues: integerTickValues,
              legend: primaryMetric.unit,
              legendPosition: "middle",
              legendOffset: -48,
            }}
            tooltip={() => null}
            onMouseEnter={({ id, indexValue, value }) => {
              const tooltipMetric = pureBarChart
                ? metrics.find((metric) => metric.id === id) || primaryMetric
                : primaryMetric;
              setBarTooltip({ indexValue, value, metric: tooltipMetric });
            }}
            onMouseLeave={() => setBarTooltip(null)}
            theme={nivoTheme}
            layers={!pureBarChart && metrics.length > 1
              ? ["grid", "axes", "bars", (props) => (
                  <ComparisonLineLayer {...props} metrics={metrics} primaryMetric={primaryMetric} />
                )]
              : ["grid", "axes", "bars"]}
          />
          {barTooltip && (
            <div className="evaluation-analytics-comparison-tooltip-overlay">
              <div className="evaluation-analytics-chart-tooltip">
                <strong>{barTooltip.indexValue}</strong>
                <span>
                  {barTooltip.metric.label}: {formatChartValue(barTooltip.value)} {barTooltip.metric.unit}
                </span>
              </div>
            </div>
          )}
        </div>
        <div className="evaluation-analytics-chart-legend" aria-label={`${sectionTitle || "Comparison chart"} legend`}>
          <div>
            {metrics.map((metric) => {
              const lineIndex = metrics.filter((item) => item.id !== primaryMetric.id).findIndex((item) => item.id === metric.id);
              const color = pureBarChart
                ? CHART_COLORS[metrics.findIndex((item) => item.id === metric.id) % CHART_COLORS.length]
                : metric.id === primaryMetric.id
                  ? CHART_COLORS[0]
                  : CHART_COLORS[(lineIndex + 1) % CHART_COLORS.length];
              const legendLabel = `${metric.shortLabel}${pureBarChart ? "" : metric.id === primaryMetric.id ? " (bar)" : " (line)"}`;

              return (
                <span key={metric.id} title={legendLabel}>
                  <i style={{ background: color }} />
                  <span>{legendLabel}</span>
                </span>
              );
            })}
          </div>
        </div>
      </div>
    </>
  );

  const renderRadarChart = (isFullView = false) => (
    <>
      {!isFullView && (
        <div className="evaluation-analytics-radar-card__actions">
          {renderExpandButton("radar", `${sectionTitle || "Performance"} radar chart`)}
        </div>
      )}
      <div className="evaluation-analytics-radar-content">
        <div
          className={`evaluation-analytics-radar-chart${isFullView ? "" : " is-click-expandable"}`}
          role={isFullView ? undefined : "button"}
          tabIndex={isFullView ? undefined : 0}
          aria-label={isFullView ? undefined : `Expand ${sectionTitle || "performance"} radar chart`}
          onClick={isFullView ? undefined : () => setExpandedChart("radar")}
          onKeyDown={isFullView ? undefined : (event) => {
            if (event.key === "Enter" || event.key === " ") {
              event.preventDefault();
              setExpandedChart("radar");
            }
          }}
          onMouseLeave={() => setRadarTooltip(null)}
        >
          <ResponsiveRadar
            data={radarData}
            keys={activeRadarCategories}
            indexBy="metric"
            maxValue={100}
            margin={isCompactRadar
              ? { top: 85, right: 92, bottom: 55, left: 92 }
              : { top: 85, right: 115, bottom: 45, left: 115 }}
            gridLabelOffset={isCompactRadar ? 14 : 20}
            dotSize={7}
            dotBorderWidth={1}
            colors={radarColors}
            fillOpacity={0.12}
            borderWidth={2}
            blendMode="normal"
            theme={RADAR_THEME}
            sliceTooltip={renderRadarTooltipTracker}
            layers={["grid", "layers", "slices", "dots"]}
            role="img"
            ariaLabel={`${sectionTitle || "Performance"} radar chart`}
          />
          {radarTooltip && (
            <div className="evaluation-analytics-radar-tooltip-overlay">
              <RadarValueTooltip
                {...radarTooltip}
                metrics={metrics}
                compact={isCompactRadar}
              />
            </div>
          )}
        </div>
        <fieldset className="evaluation-analytics-chipset-selector" aria-label="Select chipsets to compare">
          {radarCategories.map((category, index) => {
            const isSelected = selectedRadarCategories.includes(category);

            return (
              <label key={category}>
                <input
                  type="checkbox"
                  checked={isSelected}
                  onChange={() => toggleRadarCategory(category)}
                  style={{ accentColor: CHART_COLORS[index % CHART_COLORS.length] }}
                />
                <span>{category}</span>
              </label>
            );
          })}
        </fieldset>
      </div>
    </>
  );

  return (
    <>
      <div className={`evaluation-analytics-optimized-grid ${isPerformanceByChipset || isEvaluationsByChipset ? "evaluation-analytics-optimized-grid--single" : ""}`}>
        {showComparisonChart && (
          <div className="container-card evaluation-analytics-comparison-card">
            {renderComparisonChart()}
          </div>
        )}

        {showRadarChart && (
          <div className="container-card evaluation-analytics-radar-card">
            {renderRadarChart()}
          </div>
        )}
      </div>

      {expandedChart && (
        <div
          className="evaluation-analytics-full-view"
          role="dialog"
          aria-modal="true"
          aria-label={`${sectionTitle || "Evaluation analytics"} full view`}
          onClick={() => setExpandedChart(null)}
        >
          <div
            className="container-card evaluation-analytics-full-view__content"
            onClick={(event) => event.stopPropagation()}
          >
            <button
              type="button"
              className="evaluation-analytics-full-view__close"
              aria-label="Close full view"
              title="Close full view"
              onClick={() => setExpandedChart(null)}
            >
              <FontAwesomeIcon icon={faClose} />
            </button>
            <div className="evaluation-analytics-full-view__chart">
              {expandedChart === "comparison"
                ? renderComparisonChart(true)
                : renderRadarChart(true)}
            </div>
          </div>
        </div>
      )}
    </>
  );
}

function StatisticsTable({ table }) {
  const columns = table?.columns || [];
  const rows = table?.data || [];

  if (!columns.length) return null;

  return (
    <div className="evaluation-analytics-table-wrap">
      <table className="evaluation-analytics-table">
        <thead>
          <tr>
            {columns.map((column) => (
              <th key={column.accessor}>{column.Header}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.length ? rows.map((row, rowIndex) => (
            <tr key={row.id || rowIndex}>
              {columns.map((column) => (
                <td key={column.accessor}>{row[column.accessor] ?? "--"}</td>
              ))}
            </tr>
          )) : (
            <tr>
              <td colSpan={columns.length} className="text-center text-muted py-4">
                No statistics available
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
}

export default function EvaluationAnalytics({ modelId, embedded = false }) {
  const [page, setPage] = useState(1);
  const [response, setResponse] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [refreshKey, setRefreshKey] = useState(0);

  useEffect(() => {
    let active = true;
    const params = new URLSearchParams({
      section: modelId ? "model_usage_statistics" : "eval_statistics",
      page: String(page),
      page_size: String(modelId ? MODEL_PAGE_SIZE : PAGE_SIZE),
      is_stream: "false",
    });
    if (modelId) {
      params.set("filters", JSON.stringify({ model_id: modelId }));
    }
    const url = `${window.ENV.API_URL}/evaluations?${params.toString()}`;

    setLoading(true);
    setError("");

    getDataFromApi(url)
      .then((result) => {
        if (!active) return;
        if (result?.detail) throw new Error(typeof result.detail === "string" ? result.detail : "Unable to load analytics");
        setResponse(result);
      })
      .catch((requestError) => {
        if (!active) return;
        setError(requestError?.message || "Unable to load evaluation analytics.");
      })
      .finally(() => {
        if (active) setLoading(false);
      });

    return () => {
      active = false;
    };
  }, [modelId, page, refreshKey]);

  const sections = useMemo(() => getStatisticsSections(response), [response]);
  const totalPages = Math.max(Number(response?.total_pages) || 1, 1);

  if (loading) {
    return (
      <div className="evaluation-analytics-status">
        <Loader />
        <span className="text-muted">Loading evaluation analytics...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="evaluation-analytics-status">
        <FontAwesomeIcon icon={faChartColumn} className="evaluation-analytics-empty-icon" />
        <h5>Analytics unavailable</h5>
        <p className="text-muted mb-3">{error}</p>
        <Button color="primary" onClick={() => setRefreshKey((key) => key + 1)}>
          <FontAwesomeIcon icon={faRotateRight} className="me-2" />
          Retry
        </Button>
      </div>
    );
  }

  return (
    <div className={`evaluation-analytics-page ${modelId ? "evaluation-analytics-page--model" : ""} ${embedded ? "evaluation-analytics-page--embedded" : ""}`}>
      <div className="evaluation-analytics-title-block">
        <h3 className="fw-bold mb-1">{modelId ? "Model Evaluation Analytics" : "Global Evaluation Analytics"}</h3>
        <p className="text-muted mb-0">
          {modelId
            ? "Evaluation activity for this model across chipsets and devices."
            : "Evaluation activity across all models, chipsets, devices, and users."}
        </p>
      </div>

      {sections.length ? sections.map((section) => (
        <section className="container-card evaluation-analytics-section" key={section.key}>
          {!modelId && (
            <div className="evaluation-analytics-section-header">
              <div>
                <h5 className="mb-1 fw-bold">{section.title || section.key}</h5>
                {section.generated_on && (
                  <span className="text-muted small">Generated on {formatGeneratedOn(section.generated_on)}</span>
                )}
              </div>
            </div>
          )}

          {!modelId && !!section.kpis?.length && (
            <div className={`evaluation-analytics-kpis ${section.kpis.length === 5 ? "evaluation-analytics-kpis--five" : ""}`}>
              {section.kpis.map((kpi) => (
                <div className="container-card evaluation-analytics-kpi" key={kpi.label}>
                  <span className="text-muted small fw-semibold">{kpi.label}</span>
                  <strong
                    className={String(kpi.value ?? "").length > 16 ? "evaluation-analytics-kpi-value--long" : ""}
                    title={String(kpi.value ?? "--")}
                  >
                    {kpi.value ?? "--"}
                  </strong>
                </div>
              ))}
            </div>
          )}

          {modelId ? (
            <OptimizedModelCharts
              charts={section.charts}
              sectionTitle={section.title || section.key.replaceAll("_", " ")}
            />
          ) : (
            <div className="evaluation-analytics-charts-grid">
              {Object.entries(section.charts || {}).map(([chartKey, chart]) => (
                <StatisticsChart chartKey={chartKey} chart={chart} key={chartKey} />
              ))}
            </div>
          )}

          {Object.entries(section.tables || {}).map(([tableKey, table]) => (
            <StatisticsTable table={table} key={tableKey} />
          ))}
        </section>
      )) : (
        <div className="evaluation-analytics-status">
          <FontAwesomeIcon icon={faChartColumn} className="evaluation-analytics-empty-icon" />
          <h5>No analytics found</h5>
          <p className="text-muted">There are no evaluation statistics to display.</p>
        </div>
      )}

      {totalPages > 1 && (
        <div className="evaluation-analytics-pagination">
          <Button color="secondary" outline disabled={page <= 1} onClick={() => setPage((current) => Math.max(current - 1, 1))}>
            Previous
          </Button>
          <span className="text-muted">Page {page} of {totalPages}</span>
          <Button color="secondary" outline disabled={page >= totalPages} onClick={() => setPage((current) => Math.min(current + 1, totalPages))}>
            Next
          </Button>
        </div>
      )}
    </div>
  );
}
